import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import apiService from '../../../services/api';
import { useToast } from '../../../hooks/useToast';
import {
  formatAbsenceBlock,
  getPresenceLabelForDay,
  getDateExceptionPlagesForYmd,
} from '../../../utils/equipePlanningUtils';
import { formatParisDayHeader } from '../../../utils/equipeWeekParis';

function overlapsYmd(absence, ymd) {
  if (!absence?.dateDebut) return false;
  const start = new Date(absence.dateDebut);
  const end = new Date(absence.dateFin || absence.dateDebut);
  const dayStart = new Date(`${ymd}T00:00:00`);
  const dayEnd = new Date(`${ymd}T23:59:59`);
  return start <= dayEnd && end >= dayStart;
}

function toDatetimeLocalValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const EMPTY_FORM = {
  dateDebutLocal: '',
  dateFinLocal: '',
  motif: '',
  type: '',
};

export default function PresencesDateExceptionDrawer({
  open,
  onClose,
  member,
  day,
  horairesConfig,
  absences = [],
  dateExceptionsByDate = {},
  onSaved,
}) {
  const { toast } = useToast();
  const instructeurId = member?.instructeurId;
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showHoursForm, setShowHoursForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [hoursPlages, setHoursPlages] = useState([{ heureDebut: '09:00', heureFin: '17:00' }]);

  const ymd = day?.ymd;
  const jourSemaine = day?.jourSemaine;
  const habitual = useMemo(
    () => (horairesConfig && jourSemaine != null ? getPresenceLabelForDay(horairesConfig, jourSemaine) : null),
    [horairesConfig, jourSemaine],
  );

  const dayAbsences = useMemo(
    () => (absences || []).filter((a) => ymd && overlapsYmd(a, ymd)),
    [absences, ymd],
  );

  const dayOverridePlages = useMemo(
    () => getDateExceptionPlagesForYmd(dateExceptionsByDate, ymd),
    [dateExceptionsByDate, ymd],
  );
  const hasHoursOverride = dayOverridePlages !== undefined;

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
    setShowHoursForm(false);
  }, []);

  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm, ymd, member?.id]);

  const presetFullDay = () => {
    if (!ymd) return;
    setForm({
      dateDebutLocal: `${ymd}T00:00`,
      dateFinLocal: `${ymd}T23:59`,
      motif: 'Absence',
      type: '',
    });
    setEditingId(null);
    setShowForm(true);
  };

  const presetPartialDay = () => {
    if (!ymd) return;
    setForm({
      dateDebutLocal: `${ymd}T09:00`,
      dateFinLocal: `${ymd}T12:00`,
      motif: 'Indisponibilité',
      type: '',
    });
    setEditingId(null);
    setShowForm(true);
  };

  const presetConge = () => {
    if (!ymd) return;
    setForm({
      dateDebutLocal: `${ymd}T00:00`,
      dateFinLocal: `${ymd}T23:59`,
      motif: 'Congés',
      type: 'CONGE',
    });
    setEditingId(null);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!instructeurId) return;
    const dateDebut = fromDatetimeLocalValue(form.dateDebutLocal);
    const dateFin = fromDatetimeLocalValue(form.dateFinLocal);
    if (!dateDebut || !dateFin) {
      toast.error('Dates invalides');
      return;
    }
    const payload = {
      dateDebut,
      dateFin,
      motif: form.motif.trim() || null,
      type: form.type.trim() || null,
    };
    setSaving(true);
    try {
      if (editingId) {
        await apiService.updateInstructeurIndisponibilite(instructeurId, editingId, payload);
        toast.success('Exception mise à jour');
      } else {
        await apiService.createInstructeurIndisponibilite(instructeurId, payload);
        toast.success('Exception enregistrée');
      }
      resetForm();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!instructeurId || !id) return;
    setSaving(true);
    try {
      await apiService.deleteInstructeurIndisponibilite(instructeurId, id);
      toast.success('Exception supprimée');
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Suppression impossible');
    } finally {
      setSaving(false);
    }
  };

  const handleRestoreHabitualHours = async () => {
    if (!instructeurId || !ymd || !hasHoursOverride) return;
    setSaving(true);
    try {
      await apiService.deleteInstructeurHoraireDateException(instructeurId, ymd);
      toast.success('Horaires habituels rétablis pour cette date');
      resetForm();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Action impossible');
    } finally {
      setSaving(false);
    }
  };

  const openHoursForm = () => {
    setShowForm(false);
    if (hasHoursOverride && dayOverridePlages?.length) {
      setHoursPlages(
        dayOverridePlages.map((p) => ({
          heureDebut: p.heureDebut || '09:00',
          heureFin: p.heureFin || '17:00',
        })),
      );
    } else {
      setHoursPlages([{ heureDebut: '10:00', heureFin: '17:00' }]);
    }
    setShowHoursForm(true);
  };

  const handleSaveHours = async (e) => {
    e.preventDefault();
    if (!instructeurId || !ymd) return;
    const plages = hoursPlages
      .map((p) => ({
        heureDebut: p.heureDebut?.trim(),
        heureFin: p.heureFin?.trim(),
      }))
      .filter((p) => p.heureDebut && p.heureFin);
    if (!plages.length) {
      toast.error('Ajoutez au moins une plage horaire');
      return;
    }
    setSaving(true);
    try {
      await apiService.upsertInstructeurHoraireDateException(instructeurId, ymd, plages);
      toast.success('Horaires exceptionnels enregistrés');
      resetForm();
      onSaved?.();
    } catch (err) {
      toast.error(err.message || 'Enregistrement impossible');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.id);
    setShowForm(true);
    setForm({
      dateDebutLocal: toDatetimeLocalValue(a.dateDebut),
      dateFinLocal: toDatetimeLocalValue(a.dateFin),
      motif: a.motif || '',
      type: a.type || '',
    });
  };

  if (!open || !member || !day) return null;

  const dateLabel = formatParisDayHeader(day.date);

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-slate-900/20"
        aria-label="Fermer"
        onClick={onClose}
      />
      <aside
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[92vh] w-full flex-col rounded-t-3xl border border-gray-100 bg-white shadow-2xl sm:inset-y-0 sm:left-auto sm:right-0 sm:max-h-none sm:w-[min(480px,92vw)] sm:rounded-none sm:border-y-0 sm:border-l"
        aria-labelledby="presences-date-exception-title"
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-violet-600">
              Exception ponctuelle
            </p>
            <h2 id="presences-date-exception-title" className="text-lg font-bold text-gray-900">
              {member.prenom} {member.nom}
            </h2>
            <p className="mt-0.5 text-sm text-gray-700">{dateLabel}</p>
            <p className="mt-2 rounded-lg border border-violet-100 bg-violet-50/80 px-3 py-2 text-xs text-violet-900">
              Exception pour le{' '}
              <span className="font-semibold">{dateLabel.toLowerCase()}</span> uniquement — cela ne
              modifie pas le planning récurrent (mercredis futurs, etc.).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
          {!member.isIntervenant || !instructeurId ? (
            <p className="text-sm text-gray-600">
              Cette personne ne réalise pas de prestations. Les exceptions de présence concernent les
              professionnels opérationnels.
            </p>
          ) : (
            <>
              <section className="rounded-xl border border-gray-100 bg-gray-50/80 px-3 py-3">
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  Habituel ce jour de semaine
                </h3>
                <p className="mt-1 text-sm font-medium text-gray-900">
                  {habitual || 'Aucune plage habituelle'}
                </p>
                <p className="mt-2 text-[11px] text-gray-500">
                  Pour modifier le planning récurrent :{' '}
                  <Link
                    to={`/equipe?member=${encodeURIComponent(member.id)}`}
                    className="font-semibold text-violet-700 hover:underline"
                  >
                    Gestion de l&apos;équipe → Planning
                  </Link>
                </p>
              </section>

              <section className="rounded-xl border border-violet-100 bg-violet-50/40 px-3 py-3">
                <h3 className="text-xs font-bold uppercase tracking-wide text-violet-700">
                  Horaires exceptionnels (cette date)
                </h3>
                {hasHoursOverride ? (
                  <p className="mt-1 text-sm font-medium text-violet-950">
                    {(dayOverridePlages || [])
                      .map((p) => `${p.heureDebut} → ${p.heureFin}`)
                      .join(' · ')}
                  </p>
                ) : (
                  <p className="mt-1 text-sm text-gray-600">Horaires habituels appliqués.</p>
                )}
              </section>

              <section>
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  Absences / indisponibilités sur cette date
                </h3>
                {dayAbsences.length === 0 ? (
                  <p className="mt-2 text-sm text-gray-500">Aucune exception — horaires habituels appliqués.</p>
                ) : (
                  <ul className="mt-2 space-y-2">
                    {dayAbsences.map((a) => {
                      const { title, detail } = formatAbsenceBlock(a.dateDebut, a.dateFin, a.motif);
                      return (
                        <li
                          key={a.id}
                          className="rounded-xl border border-amber-100 bg-amber-50/60 px-3 py-2 text-sm"
                        >
                          <p className="font-semibold text-amber-950">{title}</p>
                          {detail ? <p className="text-xs text-amber-900">{detail}</p> : null}
                          <div className="mt-2 flex gap-3 text-xs font-semibold">
                            <button
                              type="button"
                              className="text-violet-700 hover:underline"
                              onClick={() => startEdit(a)}
                              disabled={saving}
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              className="text-red-600 hover:underline"
                              onClick={() => handleDelete(a.id)}
                              disabled={saving}
                            >
                              Supprimer
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {!showForm && !showHoursForm ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={openHoursForm}
                    className="rounded-lg border border-violet-300 bg-violet-600 px-3 py-2 text-xs font-semibold text-white hover:bg-violet-700"
                  >
                    Modifier les horaires de cette journée
                  </button>
                  <button
                    type="button"
                    onClick={presetFullDay}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    Absence (journée)
                  </button>
                  <button
                    type="button"
                    onClick={presetPartialDay}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    Indisponibilité (plage)
                  </button>
                  <button
                    type="button"
                    onClick={presetConge}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 hover:bg-gray-50"
                  >
                    Congé (journée)
                  </button>
                  {hasHoursOverride ? (
                    <button
                      type="button"
                      onClick={handleRestoreHabitualHours}
                      disabled={saving}
                      className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-900 hover:bg-emerald-100"
                    >
                      Revenir aux horaires habituels
                    </button>
                  ) : null}
                </div>
              ) : showHoursForm ? (
                <form onSubmit={handleSaveHours} className="space-y-3 rounded-xl border border-violet-200 bg-violet-50/50 p-4">
                  <p className="text-sm font-semibold text-gray-900">
                    Horaires exceptionnels pour le {dateLabel.toLowerCase()} uniquement
                  </p>
                  <p className="text-xs text-gray-600">
                    Habituel : {habitual || '—'}
                  </p>
                  <p className="text-xs text-violet-900">
                    Cette modification s&apos;applique uniquement à cette date. Le planning habituel
                    reste inchangé.
                  </p>
                  <div className="space-y-2">
                    {hoursPlages.map((plage, idx) => (
                      <div key={idx} className="flex flex-wrap items-end gap-2">
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-500">Début</label>
                          <input
                            type="time"
                            required
                            value={plage.heureDebut}
                            onChange={(e) =>
                              setHoursPlages((prev) =>
                                prev.map((p, i) =>
                                  i === idx ? { ...p, heureDebut: e.target.value } : p,
                                ),
                              )
                            }
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-500">Fin</label>
                          <input
                            type="time"
                            required
                            value={plage.heureFin}
                            onChange={(e) =>
                              setHoursPlages((prev) =>
                                prev.map((p, i) =>
                                  i === idx ? { ...p, heureFin: e.target.value } : p,
                                ),
                              )
                            }
                            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-sm"
                          />
                        </div>
                        {hoursPlages.length > 1 ? (
                          <button
                            type="button"
                            className="text-xs font-semibold text-red-600"
                            onClick={() =>
                              setHoursPlages((prev) => prev.filter((_, i) => i !== idx))
                            }
                          >
                            Retirer
                          </button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className="text-xs font-semibold text-violet-700 hover:underline"
                    onClick={() =>
                      setHoursPlages((prev) => [...prev, { heureDebut: '14:00', heureFin: '18:00' }])
                    }
                  >
                    + Ajouter une plage
                  </button>
                  <div className="flex flex-wrap gap-2 pt-1">
                    <button
                      type="submit"
                      disabled={saving}
                      className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                    >
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSave} className="space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-900">
                    {editingId ? 'Modifier l’exception' : 'Nouvelle exception'}
                  </p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Début</label>
                      <input
                        type="datetime-local"
                        required
                        value={form.dateDebutLocal}
                        onChange={(e) => setForm((p) => ({ ...p, dateDebutLocal: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-gray-500">Fin</label>
                      <input
                        type="datetime-local"
                        required
                        value={form.dateFinLocal}
                        onChange={(e) => setForm((p) => ({ ...p, dateFinLocal: e.target.value }))}
                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-500">Motif</label>
                    <input
                      value={form.motif}
                      onChange={(e) => setForm((p) => ({ ...p, motif: e.target.value }))}
                      placeholder="Absence, congé, indisponibilité…"
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60"
                    >
                      Enregistrer
                    </button>
                    <button
                      type="button"
                      onClick={resetForm}
                      className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
